import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import {
    accountActions,
    alertActions,
    coverManagerActions,
    facebookActions,
    googleActions,
    locationsProfilesActions,
    vtexActions,
} from '@actions';
import { ModalConfirm } from '@components/Modal';
import MoreMenu from '@components/MoreMenu';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { LocationIntegrationStatus } from '@constants/companies';
import { LOCALE } from '@constants/locale';
import { LocationIntegrationName, ProfileName, AccIntegrationName } from '@constants/integrations';
import { handleApiError } from '@helpers/api';
import { isIntegrationConnected, isIntegrationPending, getActiveIntegrations } from '@helpers/integrations';
import { createProxyObject } from '@helpers/createProxyObject';
import { useTranslation } from '@hooks';
import { RoutePath } from '@constants/routes';
import { companyService, googleIntegrationService, tiktokIntegrationService, xIntegrationService } from '@services';
import ManageProfileModal from '../ManageProfileModal';
import VerificationModal from '../VerificationModal';
import CreateGoogleProfileModal from './CreateGoogleProfileModal';
import SocialAccountPickerModal from './SocialAccountPickerModal';
import GenericIntegration from './GenericIntegration';
import IFoodIntegration from './IFoodIntegration';
import XIntegration from './XIntegration';
import LinkedInIntegration from './LinkedInIntegration';
import { Container, Point, StyledTable, TableText } from './styles';

const IntegrationDrawer = props => {
    switch (Number(props.integrationType)) {
        case LocationIntegrationName.iFood:
            return <IFoodIntegration {...props} />;
        case LocationIntegrationName.X:
            return <XIntegration {...props} />;
        case LocationIntegrationName.LinkedIn:
            return <LinkedInIntegration {...props} />;
        case LocationIntegrationName.Google:
        case LocationIntegrationName.Facebook:
        case LocationIntegrationName.VTEX:
        case LocationIntegrationName.CoverManager:
            return <GenericIntegration {...props} />;
        default:
            return null;
    }
};

const getColumnNames = t => [
    {
        key: 0,
        component: <Typography variant="subtitle2">{t('SettingsCompanies.integrationName')}</Typography>,
    },
    {
        key: 1,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.status')}
            </Typography>
        ),
    },
    {
        key: 2,
        component: <div />,
    },
];

const Action = { Connect: 0, Disconnect: 1, Manage: 2, Create: 3, Verify: 4 };

const STATUS_PRIORITY = {
    [LocationIntegrationStatus.Connected]: 3,
    [LocationIntegrationStatus.Pending]: 2,
    [LocationIntegrationStatus.Unverified]: 1,
    [LocationIntegrationStatus.Disconnected]: 0,
};

const WildCardIntegrations = [
    LocationIntegrationName.CoverManager,
    LocationIntegrationName.iFood,
    LocationIntegrationName.VTEX,
];

const getRowsData = (
    account,
    company,
    onActionClick,
    anchor,
    handleOpenEl,
    handleCloseEl,
    hasMissingRequiredFields,
) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getIntegrationsConnected = (type, profileType) => {
        const officialIntegrationConnected = isIntegrationConnected(account, profileType);
        // Historical duplicate location-integration rows can share a type; pick the one with the
        // strongest status deterministically so a stale Disconnected row never masks an active one.
        const integration = (company?.integrations || [])
            .filter(i => i.type === type)
            .sort((a, b) => (STATUS_PRIORITY[b?.status] ?? -1) - (STATUS_PRIORITY[a?.status] ?? -1))[0];
        return { officialIntegrationConnected, integration };
    };

    const getStatus = integration => {
        switch (integration?.status) {
            case LocationIntegrationStatus.Disconnected:
                return { color: 'red', text: t('common.disconnected') };
            case LocationIntegrationStatus.Unverified:
                return { color: 'red', text: t('common.unverified') };
            case LocationIntegrationStatus.Pending:
                return { color: 'yellow', text: t('common.inProgress') };
            case LocationIntegrationStatus.Connected:
                return {
                    color: 'green',
                    text: t('SettingsCompanies.connected'),
                };
            default:
                return {
                    color: '',
                    text: t('SettingsCompanies.notConnected'),
                };
        }
    };

    return [
        { name: 'Google', type: LocationIntegrationName.Google, profileType: ProfileName.GoogleOfficial },
        {
            name: 'Facebook & Instagram',
            type: LocationIntegrationName.Facebook,
            profileType: ProfileName.FacebookOfficial,
        },
        ...(account?.locale === LOCALE.SPAIN
            ? [
                  {
                      name: 'CoverManager',
                      type: LocationIntegrationName.CoverManager,
                      profileType: ProfileName.CoverManager,
                  },
              ]
            : []),
        ...(account?.locale === LOCALE.BRAZIL
            ? [{ name: 'iFood', type: LocationIntegrationName.iFood, profileType: ProfileName.iFood }]
            : []),
        { name: 'VTEX', type: LocationIntegrationName.VTEX, profileType: ProfileName.VTEX },
        { name: 'TikTok', type: LocationIntegrationName.Tiktok, profileType: ProfileName.Tiktok },
        { name: 'X', type: LocationIntegrationName.X, profileType: ProfileName.X },
        { name: 'LinkedIn', type: LocationIntegrationName.LinkedIn, profileType: ProfileName.LinkedIn },
    ]
        .filter(({ type, profileType }) => {
            if (!WildCardIntegrations.includes(type)) {
                return true;
            }
            const { integration, officialIntegrationConnected } = getIntegrationsConnected(type, profileType);
            return officialIntegrationConnected || integration;
        })
        .map(({ name, type, profileType, callout }, index) => {
            const { integration, officialIntegrationConnected } = getIntegrationsConnected(type, profileType);
            const isGoogleOrFb = type === LocationIntegrationName.Google || type === LocationIntegrationName.Facebook;
            const isTiktok = type === LocationIntegrationName.Tiktok;
            const isX = type === LocationIntegrationName.X;
            const isLinkedIn = type === LocationIntegrationName.LinkedIn;
            const isDisconnected = integration?.status === LocationIntegrationStatus.Disconnected || !integration;
            // Trumps the location row: re-authorize flips stale rows back to Connected, so their
            // status lies while merchants are absent.
            const ifoodAccountPending =
                type === LocationIntegrationName.iFood && isIntegrationPending(account, AccIntegrationName.iFood);
            const getMenu = () => {
                if (!integration) {
                    const isGoogle = type === LocationIntegrationName.Google;
                    const isFacebook = type === LocationIntegrationName.Facebook;

                    const googleDisabledNoAccount = isGoogle && !officialIntegrationConnected;
                    const googleDisabledMissingFields = isGoogle && hasMissingRequiredFields;
                    const facebookDisabledNoAccount = isFacebook && !officialIntegrationConnected;
                    const tiktokDisabledNoAccount = isTiktok && !officialIntegrationConnected;
                    const xDisabledNoAccount = isX && !officialIntegrationConnected;
                    const linkedInDisabledNoAccount = isLinkedIn && !officialIntegrationConnected;

                    const connectDisabled =
                        googleDisabledNoAccount ||
                        googleDisabledMissingFields ||
                        facebookDisabledNoAccount ||
                        tiktokDisabledNoAccount ||
                        xDisabledNoAccount ||
                        linkedInDisabledNoAccount;

                    const connectTooltip = isGoogle
                        ? googleDisabledNoAccount
                            ? t('SettingsCompanies.googleAccountRequired')
                            : googleDisabledMissingFields
                              ? t('validation.completeAllFields')
                              : ''
                        : isFacebook
                          ? facebookDisabledNoAccount
                              ? t('SettingsCompanies.facebookAccountRequired')
                              : ''
                          : isTiktok
                            ? tiktokDisabledNoAccount
                                ? t('SettingsCompanies.tiktokAccountRequired')
                                : ''
                            : isX
                              ? xDisabledNoAccount
                                  ? t('SettingsCompanies.xAccountRequired')
                                  : ''
                              : isLinkedIn
                                ? linkedInDisabledNoAccount
                                    ? t('SettingsCompanies.linkedinAccountRequired')
                                    : ''
                                : '';

                    return [
                        {
                            id: 0,
                            title: ifoodAccountPending ? t('buttons.viewStatus') : t('buttons.connectProfile'),
                            onClick: !officialIntegrationConnected
                                ? () => navigate(RoutePath.AccountSettingsIntegrations)
                                : () => onActionClick(type, profileType, Action.Connect, integration?.id),
                            disabled: connectDisabled,
                            tooltip: connectTooltip,
                        },
                        ...(isGoogle
                            ? [
                                  {
                                      id: 3,
                                      title: t('buttons.createNewProfile'),
                                      onClick: () => onActionClick(type, profileType, Action.Create, integration?.id),
                                      disabled: googleDisabledNoAccount || googleDisabledMissingFields,
                                      tooltip: googleDisabledNoAccount
                                          ? t('SettingsCompanies.googleAccountRequired')
                                          : googleDisabledMissingFields
                                            ? t('validation.completeAllFields')
                                            : '',
                                  },
                              ]
                            : []),
                    ];
                } else {
                    const isUnverifiedGoogle =
                        type === LocationIntegrationName.Google &&
                        integration?.status === LocationIntegrationStatus.Unverified;
                    return [
                        ...(ifoodAccountPending
                            ? [
                                  {
                                      id: 0,
                                      title: t('buttons.viewStatus'),
                                      onClick: () => onActionClick(type, profileType, Action.Connect, integration?.id),
                                  },
                              ]
                            : []),
                        ...(isDisconnected && !ifoodAccountPending
                            ? [
                                  {
                                      id: 0,
                                      title: t('buttons.connectProfile'),
                                      onClick: () => navigate(RoutePath.AccountSettingsIntegrations),
                                  },
                              ]
                            : []),
                        ...(isGoogleOrFb && !isUnverifiedGoogle && !isDisconnected
                            ? [
                                  {
                                      id: 0,
                                      title: t('buttons.manageProfile'),
                                      onClick: () => onActionClick(type, profileType, Action.Manage, integration?.id),
                                  },
                              ]
                            : []),
                        ...(isUnverifiedGoogle
                            ? [
                                  {
                                      id: 4,
                                      title: t('common.verify'),
                                      onClick: () => onActionClick(type, profileType, Action.Verify, integration?.id),
                                  },
                              ]
                            : []),
                        {
                            id: 1,
                            title: t('buttons.disconnect'),
                            onClick: () => onActionClick(type, profileType, Action.Disconnect, integration?.id),
                        },
                    ];
                }
            };
            const { color, text } = ifoodAccountPending
                ? { color: 'yellow', text: t('common.inProgress') }
                : getStatus(integration);
            return [
                {
                    component: (
                        <TableText variant="subtitle2" align="left">
                            {name}
                            {callout && typeof callout === 'string' && (
                                <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                                    <Tooltip title={callout} placement="top" enterTouchDelay={0}>
                                        <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                                    </Tooltip>
                                </IconButton>
                            )}
                        </TableText>
                    ),
                },
                {
                    component: (
                        <TableText variant="body2" align="left">
                            <Point type={color} />
                            {text}
                        </TableText>
                    ),
                },
                {
                    component: (
                        <MoreMenu
                            id={index}
                            menu={getMenu()}
                            handleOpenEl={handleOpenEl}
                            handleCloseEl={handleCloseEl}
                            anchor={anchor}
                        />
                    ),
                },
            ];
        });
};

const ImplicitIntegrations = [
    LocationIntegrationName.Google,
    LocationIntegrationName.Facebook,
    LocationIntegrationName.CoverManager,
    LocationIntegrationName.iFood,
    LocationIntegrationName.VTEX,
    LocationIntegrationName.X,
    LocationIntegrationName.LinkedIn,
];

const IntegrationsTable = ({ account, selectedCompany, setSelectedCompany, isInitialFormValid }) => {
    const { t } = useTranslation();
    const [integration, setIntegration] = useState({ open: false, type: null, id: null, googleAccountId: null });
    const [isDisconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [isProfileModalOpen, setProfileModalOpen] = useState(false);
    const [isVerificationModalOpen, setVerificationModalOpen] = useState(false);
    const [createModal, setCreateModal] = useState({
        open: false,
        defaults: null,
        integrations: [],
        initialIntegrationId: null,
        initialAccounts: [],
    });
    const [creating, setCreating] = useState(false);
    const [socialPicker, setSocialPicker] = useState({
        open: false,
        type: null,
        integrations: [],
        connecting: false,
    });
    const [anchor, setAnchor] = useState({ el: null, id: null });
    const hasMissingRequiredFields = !isInitialFormValid;
    const dispatch = useDispatch();

    const handleOpenEl = (event, id) => {
        event.stopPropagation();
        setAnchor({ id, el: event.currentTarget });
    };

    const handleCloseEl = () => setAnchor(prev => ({ ...prev, el: null }));

    const onIntegrationClick = async (integrationType, profileType, action, integrationId) => {
        handleCloseEl();
        switch (action) {
            case Action.Connect:
                if (!ImplicitIntegrations.includes(integrationType)) {
                    onConnectIntegrationExplicitly(integrationType);
                } else {
                    setIntegration(prev => ({ ...prev, open: true, type: integrationType, profileType }));
                }
                return;
            case Action.Disconnect:
                setIntegration(prev => ({ ...prev, type: integrationType, profileType, id: integrationId }));
                setDisconnectModalOpen(true);
                return;
            case Action.Manage:
                setIntegration(prev => ({ ...prev, type: integrationType, profileType, id: integrationId }));
                setProfileModalOpen(true);
                return;
            case Action.Create:
                await onCreateLocation();
                return;
            case Action.Verify: {
                const googleProfile = selectedCompany?.profiles?.find(p => p.type === ProfileName.GoogleOfficial);
                if (!googleProfile?.profileId) {
                    dispatch(alertActions.error(t('common.somethingWentWrong')));
                    return;
                }
                setIntegration(prev => ({
                    ...prev,
                    type: integrationType,
                    profileType,
                    id: googleProfile.profileId,
                    googleAccountId: googleProfile.googleAccountId,
                }));
                setVerificationModalOpen(true);
                return;
            }
            default:
                return;
        }
    };

    const IntegrationMapCb = {
        [LocationIntegrationName.Tiktok]: (locationId, integrationId) =>
            tiktokIntegrationService.connectLocation(locationId, integrationId),
        [LocationIntegrationName.X]: (locationId, integrationId) =>
            xIntegrationService.connectLocation(locationId, integrationId),
    };

    const performExplicitConnect = async (integrationType, integrationId = null) => {
        try {
            const updatedCompany = await IntegrationMapCb[integrationType](selectedCompany.companyId, integrationId);
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
            return true;
        } catch (error) {
            handleApiError(error);
            return false;
        }
    };

    const onConnectIntegrationExplicitly = async integrationType => {
        // TikTok connects directly (no preview drawer); when several TikTok accounts are connected,
        // prompt for which one so the backend never binds the location to an arbitrary account.
        if (integrationType === LocationIntegrationName.Tiktok) {
            const activeTikToks = getActiveIntegrations(account, AccIntegrationName.TikTok);
            if (activeTikToks.length > 1) {
                setSocialPicker({ open: true, type: integrationType, integrations: activeTikToks, connecting: false });
                return;
            }
            await performExplicitConnect(integrationType, activeTikToks[0]?.id ?? null);
            return;
        }
        await performExplicitConnect(integrationType);
    };

    const onSocialPickerConnect = async integrationId => {
        setSocialPicker(prev => ({ ...prev, connecting: true }));
        const success = await performExplicitConnect(socialPicker.type, integrationId);
        setSocialPicker(prev => ({ ...prev, connecting: false }));
        if (success) {
            setSocialPicker({ open: false, type: null, integrations: [], connecting: false });
        }
    };

    const DataResetMachine = createProxyObject({
        [LocationIntegrationName.CoverManager]: coverManagerActions.reset,
        [LocationIntegrationName.FacebookOfficial]: facebookActions.reset,
        [LocationIntegrationName.GoogleOfficial]: googleActions.reset,
        [LocationIntegrationName.VTEX]: vtexActions.reset,
        default: () => ({ type: 'RESET_LOCATION_INTEGRATION' }),
    });

    const disconnectIntegration = async () => {
        try {
            const updatedCompany = await companyService.disconnectIntegration(
                selectedCompany.companyId,
                integration.id,
            );
            await dispatch(accountActions.get());
            setSelectedCompany(updatedCompany);
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(DataResetMachine[integration.type]());
            if (isProfileModalOpen) {
                setProfileModalOpen(false);
            }
            setDisconnectModalOpen(false);
            setIntegration(prev => ({ ...prev, type: null, id: null }));
            dispatch(alertActions.success(t('SettingsCompanies.profileDisconnected')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const runCreate = async (integrationId, googleAccountId) => {
        try {
            const updatedLocation = await googleIntegrationService.createLocation(
                selectedCompany.companyId,
                integrationId,
                googleAccountId,
            );
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            setSelectedCompany(updatedLocation);
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
            return true;
        } catch (error) {
            handleApiError(error);
            return false;
        }
    };

    const onCreateLocation = async () => {
        try {
            const googleIntegrations = getActiveIntegrations(account, AccIntegrationName.Google);

            let defaults = null;
            try {
                defaults = await googleIntegrationService.getCreateDefaults(selectedCompany.companyId);
            } catch (error) {
                handleApiError(error);
            }

            const initialIntegrationId = defaults?.integrationId || googleIntegrations[0]?.id || null;
            const initialAccounts = initialIntegrationId
                ? await googleIntegrationService.getAccounts(initialIntegrationId)
                : [];

            // Only prompt when there is an actual choice — multiple connected Google accounts or
            // multiple location groups under the chosen account; otherwise create straight away.
            const needsChoice = googleIntegrations.length > 1 || (initialAccounts?.length || 0) > 1;
            if (!needsChoice) {
                await runCreate(initialIntegrationId, initialAccounts?.[0]?.id ?? null);
                return;
            }

            setCreateModal({
                open: true,
                defaults,
                integrations: googleIntegrations,
                initialIntegrationId,
                initialAccounts: initialAccounts || [],
            });
        } catch (error) {
            handleApiError(error);
        }
    };

    const onCreateFromModal = async (integrationId, googleAccountId) => {
        setCreating(true);
        const success = await runCreate(integrationId, googleAccountId);
        setCreating(false);
        if (success) {
            setCreateModal(prev => ({ ...prev, open: false }));
        }
    };

    const onCloseDrawer = () => setIntegration(prev => ({ ...prev, open: false, type: null }));

    const onCloseProfileModal = () => setProfileModalOpen(false);

    return (
        <>
            <Container>
                <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                    {t('SettingsCompanies.integrations')}
                </Typography>
                <TableContainer>
                    <StyledTable
                        columnNames={getColumnNames(t)}
                        rows={getRowsData(
                            account,
                            selectedCompany,
                            onIntegrationClick,
                            anchor,
                            handleOpenEl,
                            handleCloseEl,
                            hasMissingRequiredFields,
                        )}
                    />
                </TableContainer>
            </Container>
            {integration.open ? (
                <IntegrationDrawer
                    isOpen={integration.open}
                    onClose={onCloseDrawer}
                    onConnect={onCloseDrawer}
                    integrationType={integration.type}
                    profileType={integration.profileType}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                />
            ) : null}
            {isDisconnectModalOpen && (
                <ModalConfirm
                    isOpen={isDisconnectModalOpen}
                    handleClose={() => setDisconnectModalOpen(false)}
                    title={t('SettingsCompanies.disconnectIntegration')}
                    description={t('SettingsCompanies.disconnectIntegrationMessage')}
                    onPrimaryAction={disconnectIntegration}
                    primaryActionText={t('buttons.disconnect')}
                    primaryActionType="danger"
                />
            )}
            {isProfileModalOpen && (
                <ManageProfileModal
                    integrationType={integration.type}
                    open={isProfileModalOpen}
                    onCloseModal={onCloseProfileModal}
                    selectedCompany={selectedCompany}
                    setDisconnectModalOpen={setDisconnectModalOpen}
                />
            )}
            {isVerificationModalOpen && (
                <VerificationModal
                    open={isVerificationModalOpen}
                    onClose={() => setVerificationModalOpen(false)}
                    onVerified={() => dispatch(locationsProfilesActions.get(selectedCompany.companyId))}
                    profileId={integration.id}
                    googleAccountId={integration.googleAccountId}
                />
            )}
            {socialPicker.open && (
                <SocialAccountPickerModal
                    open={socialPicker.open}
                    onClose={() => setSocialPicker({ open: false, type: null, integrations: [], connecting: false })}
                    integrations={socialPicker.integrations}
                    title={t('SettingsCompanies.connectLocation')}
                    onConnect={onSocialPickerConnect}
                    connecting={socialPicker.connecting}
                />
            )}
            {createModal.open && (
                <CreateGoogleProfileModal
                    open={createModal.open}
                    onClose={() => setCreateModal(prev => ({ ...prev, open: false }))}
                    integrations={createModal.integrations}
                    defaults={createModal.defaults}
                    initialIntegrationId={createModal.initialIntegrationId}
                    initialAccounts={createModal.initialAccounts}
                    onCreate={onCreateFromModal}
                    creating={creating}
                />
            )}
        </>
    );
};

export default IntegrationsTable;
